import { useTranslation } from "react-i18next";
import { Apartment } from "../../../../../types/apartments";
import { formatCurrency, getEquipmentName } from "../../../../../util";
import { i18n } from "../../../../../i18n/i18n";
import React from "react";
import { spaceOf } from "../../../../../util/useCases";
import { roomPriceUnitKey } from "../../../../../util/roomRequestShape";
import { useBookingFlowService } from "../../useBookingFlowService";
import { formatApartmentDetailsLine } from "./formatApartmentDetailsLine";
import { Button } from "../../../../../components/ui/button";
import { STATION_TIME_ZONE, atStation } from "../../../../../util/stationTime";

/**
 * What the card reads — narrower than `Apartment` so the undated room list of
 * an object-first category (#3035) can render the same card. It has no price
 * and no opening-hours fit, because both depend on dates it does not have yet.
 */
export type ListCardData = Pick<
  Apartment,
  "featuredImage" | "name" | "langNames" | "accomodationdetail"
> &
  Partial<Pick<Apartment, "price" | "isWholeRoom" | "partOfRoom" | "openingHoursFit">>;

interface Props<T extends ListCardData> {
  data: T;
  onSelectApartment: (apartment: T) => void;
  /** Take over the window this room's opening hours allow, and search again. */
  onAdjustTime?: (apartment: T) => void;
  /**
   * No price line at all — for a list shown before any dates are known
   * (#3035). Not "price on request": that is a statement about the room, and
   * here the price is simply not known yet.
   */
  hidePrice?: boolean;
  /**
   * The zone of the room's location (#3219) — the opening-hours fit is read
   * on its clock. The parent resolves it (`zoneOfLocationId`); the default
   * zone otherwise.
   */
  timeZone?: string;
}

/** The hint for a window that runs past the opening hours, by which side. */
const openingHoursHintKey = (fit: NonNullable<Apartment["openingHoursFit"]>): string => {
  if (fit.startsEarly && fit.endsLate) return "apartment.opening_hours_both";
  return fit.startsEarly
    ? "apartment.opening_hours_starts_early"
    : "apartment.opening_hours_ends_late";
};

function ApartmentListCard<T extends ListCardData>({
  data,
  onSelectApartment,
  onAdjustTime,
  hidePrice = false,
  timeZone = STATION_TIME_ZONE,
}: Readonly<Props<T>>) {
  const { t } = useTranslation();
  const {
    featuredImage,
    name,
    price = 0,
    langNames,
    accomodationdetail,
    isWholeRoom,
    partOfRoom,
    openingHoursFit,
  } = data;
  // A partly fitting window is never booked as is: the card cannot be
  // picked, only adjusted (PO, 2026-10-02).
  const outsideHours = openingHoursFit?.status === "partial";

  /**
   * A divided room appears at its location as several cards — the whole room
   * and each part — and until #1714's presentation half there was nothing
   * relating them. A customer chose between "Rotes Rathaus", "Saal A" and
   * "Saal B" with no way to know that booking Saal A takes the whole Rathaus
   * out of the calendar, which the offering query has always made true.
   *
   * Badged in BOTH directions (PO, 2026-09-19), because one alone leaves the
   * other card unexplained. Deliberately NOT nesting, grouping or reordering —
   * those were considered and deferred post-MVP.
   *
   * The part badge names its room through `getEquipmentName`, the same resolver
   * the title above uses, so it follows the customer's language rather than
   * showing whatever the room was typed in.
   */
  const partOfLabel = partOfRoom
    ? getEquipmentName(partOfRoom.name, partOfRoom.langNames ?? undefined, "title")
    : null;
  // Which flow this is comes from the flow, not from per-tab storage —
  // see `useBookingFlowService` (QA #2764 E2).
  const flowService = useBookingFlowService();
  const flowSpace = spaceOf(flowService);

  return (
    <div
      onClick={outsideHours ? undefined : () => onSelectApartment(data)}
      className={`flex border-foreground/10 border-b mb-6 pb-3 ${outsideHours ? "" : "cursor-pointer"}`}
    >
      <div className="rounded-lg">
        <img
          className="rounded-lg h-20 w-20 object-cover"
          src={featuredImage}
          alt=""
        />
      </div>
      <div className="flex flex-col ml-4">
        <p className="text-sm font-medium text-black">
          {getEquipmentName(name, langNames, "title")}
        </p>

        {/* Grayscale through theme tokens, not an accent: tenant primary is
            reserved for the accent, and a badge is a statement of fact rather
            than a call to action — so no icon and no chevron either. */}
        {isWholeRoom && (
          <span className="mt-0.5 w-fit rounded-full bg-foreground/5 px-2 py-0.5 text-xs text-foreground/60">
            {t("apartment.whole_room")}
          </span>
        )}
        {partOfLabel && (
          <span className="mt-0.5 w-fit rounded-full bg-foreground/5 px-2 py-0.5 text-xs text-foreground/60">
            {t("apartment.part_of_room", { room: partOfLabel })}
          </span>
        )}
        <div className="flex gap-2 text-sm text-gray-600 flex-wrap">
          {flowSpace?.flow !== "event" ? (
            <span>{formatApartmentDetailsLine(accomodationdetail, t)}</span>
          ) : (
            <>
              {accomodationdetail?.kitchen > 0 && (
                <span>
                 • {accomodationdetail?.kitchen} {t("apartment.kitchen")}
                </span>
              )}

              {accomodationdetail?.terrace > 0 && (
                <span>
                  • {accomodationdetail?.terrace} {t("apartment.terrace")}
                </span>
              )}
              {accomodationdetail?.garden > 0 && (
                <span>
                  • {accomodationdetail?.garden} {t("apartment.garden")}
                </span>
              )}
              {/* Optional-chained like every sibling above. `accomodationdetail`
                  is nullable and a venue really can have none — divideRoom
                  (#1714) creates a segment's object without one — and the two
                  unguarded reads here crashed the whole list into a white
                  screen, not just this card.

                  `garden === 0` was also tested twice; the duplicate is
                  dropped rather than guessed at, since a third field was
                  clearly intended and nothing says which. */}
              {accomodationdetail?.terrace === 0 &&
                accomodationdetail?.garden === 0 && (
                  <span>
                    • {accomodationdetail?.outdoor_space}{" "}
                    {t("apartment.no_outdoor_space")}
                  </span>
                )}
            </>
          )}
        </div>
        {!hidePrice && (
        <div>
          {/* if price is not greater than 0 then show price on request */}
          {price <= 0 ? (
            <p className="text-black font-medium text-[16px]">
              {t("apartment.price_on_request")}
            </p>
          ) : (
            <p className="text-black font-medium text-[16px]">
              <span className="mr-1">{t("general.from")}</span>
              {formatCurrency(price, i18n.language)}
              &nbsp;/&nbsp;
              <span>
                {t(roomPriceUnitKey(flowService))}
              </span>
            </p>
          )}
        </div>
        )}
        {outsideHours && openingHoursFit && (
          <div className="mt-1 flex flex-col gap-2">
            <p className="text-xs text-foreground/60">
              {t(openingHoursHintKey(openingHoursFit), {
                from: openingHoursFit.openFrom,
                to: openingHoursFit.openTo,
              })}
            </p>
            {onAdjustTime && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit"
                onClick={() => onAdjustTime(data)}
              >
                {t("apartment.opening_hours_adjust", {
                  start: atStation(openingHoursFit.adjustedStart, timeZone).format("H:mm"),
                  end: atStation(openingHoursFit.adjustedEnd, timeZone).format("H:mm"),
                })}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ApartmentListCard;
